import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  AlertTitle,
  Tooltip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Checkbox,
  Collapse,
  Slider,
  InputAdornment,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import MergeIcon from '@mui/icons-material/Merge';
import ClearIcon from '@mui/icons-material/Clear';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ListIcon from '@mui/icons-material/List';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import StarIcon from '@mui/icons-material/Star';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import AutocompleteSelect from '../../Widgets/AutocompleteSelect/AutocompleteSelect';
import EnumsService from '../../../Services/enums.service';

const MySwal = withReactContent(Swal);

const mergeArrowFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

// Function to calculate the Levenshtein distance between two strings
const levenshteinDistance = (a, b) => {
  const matrix = Array.from({ length: a.length + 1 }, () => []);
  for (let i = 0; i <= a.length; i++) matrix[i][0] = i;
  for (let j = 0; j <= b.length; j++) matrix[0][j] = j;

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }
  return matrix[a.length][b.length];
};

// Function to calculate similarity score between two strings (lower score = more similar)
const getSimilarityScore = (name1, name2) => {
  const distance = levenshteinDistance(name1.toLowerCase(), name2.toLowerCase());
  const maxLength = Math.max(name1.length, name2.length);

  // If the entire name would need to be changed, don't include it
  if (distance === maxLength) {
    return -1; // This score indicates that the strings are completely different
  }

  return 1 - distance / maxLength; // Normalize the distance by string length (0 = no similarity, 1 = identical)
};

const isMeaningfulAccountCaseId = (id) => {
  if (id == null) return false;
  const trimmed = String(id).trim();
  return trimmed !== '' && trimmed !== '0';
};

/** Combines name, PalCode, and AccountCaseId signals (mirrors backend ComputeCompositeMergeScore). */
const getCompositeSimilarityScore = (cardA, cardB) => {
  const nameScore = getSimilarityScore(cardA.name || '', cardB.name || '');
  if (nameScore < 0) return -1;

  let score = nameScore;
  const palA = (cardA.palCode || '').trim();
  const palB = (cardB.palCode || '').trim();
  const validIdA = isMeaningfulAccountCaseId(cardA.accountCaseId);
  const validIdB = isMeaningfulAccountCaseId(cardB.accountCaseId);
  const idMatch = validIdA && validIdB && String(cardA.accountCaseId).trim() === String(cardB.accountCaseId).trim();
  const idConflict = validIdA && validIdB && !idMatch;
  const palMatch = palA && palB && palA.toLowerCase() === palB.toLowerCase();
  const palConflict = palA && palB && !palMatch;

  if (idMatch) score = Math.max(score, 0.92);
  else if (idConflict) score *= 0.65;

  if (palMatch) score = Math.min(1, score + 0.05);
  else if (palConflict) score *= 0.88;

  return score;
};

const formatSuggestionCardLabel = (card, t, { includeScore = false } = {}) => {
  const parts = [];
  if (card.name) parts.push(`${card.name} (${card.cardId})`);
  else parts.push(String(card.cardId));
  if (card.palCode) parts.push(`${t('PalCode')}: ${card.palCode}`);
  if (isMeaningfulAccountCaseId(card.accountCaseId)) {
    parts.push(`${t('AccountCaseId') || 'Business ID'}: ${card.accountCaseId}`);
  }
  if (includeScore && card.score != null) {
    parts.push(`${(card.score * 100).toFixed(0)}%`);
  } else if (includeScore && card.similarityScore != null) {
    parts.push(`${(card.similarityScore * 100).toFixed(0)}%`);
  }
  return parts.join(' • ');
};

const getMatchScoreColor = (score) => {
  if (score == null) return 'default';
  if (score >= 0.8) return 'success';
  if (score >= 0.6) return 'warning';
  return 'default';
};

/** Chip-based suggestion group — click a chip to set base, then use merge. */
const SuggestionGroupPicker = ({
  cards,
  baseIndex,
  onBaseChange,
  onRemoveCard,
  onUseMerge,
  onDismissGroup,
  groupBadge,
  groupBadgeColor = 'primary',
  maxGroupScore = null,
  isProcessing,
  t,
}) => {
  if (!cards?.length) return null;
  const safeBaseIdx = Math.min(baseIndex ?? 0, cards.length - 1);
  const baseCard = cards[safeBaseIdx];
  const toMergeCards = cards.filter((_, i) => i !== safeBaseIdx);
  const orderedForMerge = [baseCard, ...toMergeCards];

  return (
    <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {(groupBadge || maxGroupScore != null) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
          {groupBadge && <Chip label={groupBadge} size="small" color={groupBadgeColor} sx={{ fontWeight: 600 }} />}
          {maxGroupScore != null && (
            <Tooltip title={t('MaxSimilarityInGroupTooltip') || 'Maximum composite similarity in this group.'}>
              <Chip size="small" label={`${t('MaxSimilarity') || 'Max'}: ${maxGroupScore}`} color={groupBadgeColor} variant="outlined" />
            </Tooltip>
          )}
        </Box>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5 }}>{t('BaseCard')}:</Typography>
        <Chip
          size="small"
          label={formatSuggestionCardLabel(baseCard, t) + ` • ${t('BaseCard')}`}
          color="primary"
          variant="filled"
          onDelete={() => onRemoveCard(baseCard.cardId)}
          deleteIcon={<CloseIcon fontSize="small" sx={{ color: 'error.main' }} />}
        />
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5 }}>{t('CardsToMerge')}:</Typography>
        {toMergeCards.map((c, i) => {
          const cardScore = c.score != null ? `${(c.score * 100).toFixed(0)}%` : null;
          const chipColor = c.score != null ? getMatchScoreColor(c.score) : 'default';
          return (
            <Tooltip key={`${c.cardId}-${i}`} title={`${t('ClickToSetAsBase') || 'Click to set as base card'}${cardScore ? ` • ${t('similar')}: ${cardScore}` : ''}`}>
              <Chip
                size="small"
                label={formatSuggestionCardLabel(c, t, { includeScore: true })}
                variant="outlined"
                color={chipColor}
                onClick={() => onBaseChange(cards.indexOf(c))}
                onDelete={() => onRemoveCard(c.cardId)}
                deleteIcon={<CloseIcon fontSize="small" sx={{ color: 'error.main' }} />}
                sx={{ cursor: 'pointer' }}
              />
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => onUseMerge(orderedForMerge)}
          disabled={isProcessing || orderedForMerge.length < 2}
          sx={{ textTransform: 'none' }}
        >
          {t('UseAsMerge') || 'Use as merge'}
        </Button>
        <Tooltip title={t('Remove') || 'Remove'}>
          <IconButton size="small" onClick={onDismissGroup} aria-label={t('Remove') || 'Remove'} sx={{ p: 0.5 }}>
            <CloseIcon fontSize="small" sx={{ color: 'error.main' }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
};

const BatchMergeAccountCards = ({ isVisible, onClose, accountCardsData, handleBatchMerge, caseId, token, nameAccuracyMin: nameAccuracyMinProp = 90 }) => {
  const { t, i18n } = useTranslation();
  const direction = i18n.dir();
  const [mergeOperations, setMergeOperations] = useState([]);
  const [processedCardsData, setProcessedCardsData] = useState([]);
  const [cardsDataLoading, setCardsDataLoading] = useState(false);
  const [deleteOldCards, setDeleteOldCards] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showMiniModal, setShowMiniModal] = useState(false);
  const [miniModalSortOrder, setMiniModalSortOrder] = useState('desc');
  const [miniModalSelectedCards, setMiniModalSelectedCards] = useState([]);
  const [miniModalData, setMiniModalData] = useState(null);
  const [miniModalLoading, setMiniModalLoading] = useState(false);
  const [miniModalSearch, setMiniModalSearch] = useState('');
  const [miniModalMinSimilarity, setMiniModalMinSimilarity] = useState(90);
  const [mergeSuggestions, setMergeSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [dismissedSuggestionKeys, setDismissedSuggestionKeys] = useState(new Set());
  /** Keys like 'same-0-123' / 'similar-2-456' to hide a single card from a suggestion group */
  const [dismissedSuggestionItems, setDismissedSuggestionItems] = useState(() => new Set());
  // Filters for suggestions
  const [suggestionsFiltersOpen, setSuggestionsFiltersOpen] = useState(false);
  const [nameAccuracyMin, setNameAccuracyMin] = useState(nameAccuracyMinProp);
  const [onlyWithBusinessId, setOnlyWithBusinessId] = useState(true); // same AccountCaseId only when not empty
  const [operationsSearch, setOperationsSearch] = useState('');
  const [similarNameShowLimit, setSimilarNameShowLimit] = useState(10); // show first N similar-name groups, "Show more" adds
  const [sameCaseSectionOpen, setSameCaseSectionOpen] = useState(true);
  const [similarNamesSectionOpen, setSimilarNamesSectionOpen] = useState(true);
  const [suggestionsSearch, setSuggestionsSearch] = useState('');
  /** Per-group selected base index: groupKey -> index (0 = first card is base). */
  const [selectedBaseIndexByKey, setSelectedBaseIndexByKey] = useState(() => ({}));

  // Defer heavy processing until modal is open; run when idle so UI stays responsive
  useEffect(() => {
    if (!isVisible) {
      setProcessedCardsData([]);
      setCardsDataLoading(false);
      return;
    }
    if (!accountCardsData?.length) {
      setProcessedCardsData([]);
      setCardsDataLoading(false);
      return;
    }
    setCardsDataLoading(true);
    let cancelled = false;
    const runTransform = () => {
      if (cancelled) return;
      const transformed = accountCardsData.map(card => ({
        ...card,
        value: card.cardId,
        label: `${card.name} - ${card.cardId}${card.palCode ? ` | ${t('PalCode')}: ${card.palCode}` : ''}`,
      }));
      if (!cancelled) {
        setProcessedCardsData(transformed);
        setCardsDataLoading(false);
      }
    };
    const timeout = 120; // ms to wait for idle
    const id = typeof requestIdleCallback !== 'undefined'
      ? requestIdleCallback(runTransform, { timeout })
      : setTimeout(runTransform, 0);
    return () => {
      cancelled = true;
      if (typeof cancelIdleCallback !== 'undefined') {
        cancelIdleCallback(id);
      } else {
        clearTimeout(id);
      }
    };
  }, [isVisible, accountCardsData, t]);

  // Reset operations when modal opens (start with 0 — user adds manually or via suggestions)
  useEffect(() => {
    if (isVisible) {
      setMergeOperations([]);
    }
  }, [isVisible]);

  const allSelectedIds = useMemo(() => {
    const set = new Set();
    mergeOperations.forEach(op => {
      if (op.baseCard?.cardId) set.add(op.baseCard.cardId);
      op.cardsToMerge.forEach(card => set.add(card.cardId));
    });
    return Array.from(set);
  }, [mergeOperations]);

  const allSelectedIdsSet = useMemo(() => new Set(allSelectedIds), [allSelectedIds]);

  const getAllSelectedCardIds = useCallback(() => allSelectedIds, [allSelectedIds]);

  // Memoize dropdown option lists per operation to avoid creating new arrays every render
  const availableBaseCardsMap = useMemo(() => {
    const map = {};
    mergeOperations.forEach(op => {
      const currentBaseCardId = op.baseCard?.cardId;
      map[op.id] = processedCardsData.filter(card =>
        card.cardId === currentBaseCardId || !allSelectedIdsSet.has(card.cardId)
      );
    });
    return map;
  }, [mergeOperations, processedCardsData, allSelectedIdsSet]);

  const availableCardsForMergeMap = useMemo(() => {
    const map = {};
    mergeOperations.forEach(op => {
      const currentOperationMergeIds = new Set(op.cardsToMerge.map(c => c.cardId));
      map[op.id] = processedCardsData.filter(card =>
        currentOperationMergeIds.has(card.cardId) || !allSelectedIdsSet.has(card.cardId)
      );
    });
    return map;
  }, [mergeOperations, processedCardsData, allSelectedIdsSet]);

  // Calculate similar cards when base card is selected (memoized)
  const similarCardsMap = useMemo(() => {
    const newSimilarCardsMap = {};
    mergeOperations.forEach(operation => {
      if (operation.baseCard) {
        const selectedCard = processedCardsData.find(card => card.cardId === operation.baseCard.cardId);
        if (selectedCard) {
          const scoredCards = processedCardsData
            .filter(card => !allSelectedIdsSet.has(card.cardId))
            .map(card => ({
              ...card,
              similarityScore: getCompositeSimilarityScore(selectedCard, card),
            }))
            .filter(card => card.similarityScore > 0.35)
            .sort((a, b) => b.similarityScore - a.similarityScore)
            .slice(0, 10);
          newSimilarCardsMap[operation.id] = scoredCards;
        }
      }
    });
    return newSimilarCardsMap;
  }, [mergeOperations, processedCardsData, allSelectedIdsSet]);

  const addNewOperation = useCallback(() => {
    setMergeOperations(prev => [
      ...prev,
      { id: Date.now(), baseCard: null, cardsToMerge: [] },
    ]);
  }, []);

  const removeOperation = useCallback((id) => {
    setMergeOperations(prev => prev.filter(op => op.id !== id));
  }, []);

  const updateOperation = useCallback((id, field, value) => {
    setMergeOperations(prev =>
      prev.map(op => (op.id === id ? { ...op, [field]: value } : op))
    );
  }, []);

  const promoteToBase = useCallback((operationId, card) => {
    setMergeOperations(prev => prev.map(op => {
      if (op.id !== operationId) return op;
      const oldBase = op.baseCard;
      const newToMerge = op.cardsToMerge.filter(c => c.cardId !== card.cardId);
      if (oldBase) newToMerge.push(oldBase);
      return { ...op, baseCard: card, cardsToMerge: newToMerge };
    }));
    toast.success(t('BaseCard'), { position: 'top-center', autoClose: 1500 });
  }, [t]);

  const addSimilarCardToMerge = (operationId, card) => {
    const operation = mergeOperations.find(op => op.id === operationId);
    if (operation && !operation.cardsToMerge.find(c => c.cardId === card.cardId)) {
      updateOperation(operationId, 'cardsToMerge', [...operation.cardsToMerge, card]);
      toast.success(`${card.name} ${t('ClickToAddToMerge')}`, { position: "top-center", autoClose: 2000 });
    }
  };

  // Filtered + sorted mini modal entries from backend data
  const miniModalCardsWithSimilar = useMemo(() => {
    if (!miniModalData?.entries?.length) return [];
    let list = miniModalData.entries
      .filter(entry => {
        if (!miniModalSearch) return true;
        const q = miniModalSearch.toLowerCase();
        return (entry.name || '').toLowerCase().includes(q)
          || (entry.cardId || '').includes(q)
          || (entry.similarCards || []).some(sc => (sc.name || '').toLowerCase().includes(q) || (sc.cardId || '').includes(q));
      })
      .map(entry => ({
        card: { cardId: entry.cardId, name: entry.name, palCode: entry.palCode, accountCaseId: entry.accountCaseId },
        similarCards: (entry.similarCards || []).map(sc => ({
          cardId: sc.cardId,
          name: sc.name,
          palCode: sc.palCode,
          accountCaseId: sc.accountCaseId,
          similarityScore: sc.score,
        })),
        highestSimilarityScore: entry.similarCards?.length > 0 ? entry.similarCards[0].score : 0,
      }));
    if (miniModalSortOrder === 'asc') {
      list.sort((a, b) => a.highestSimilarityScore - b.highestSimilarityScore);
    } else {
      list.sort((a, b) => b.highestSimilarityScore - a.highestSimilarityScore);
    }
    return list;
  }, [miniModalData, miniModalSortOrder, miniModalSearch]);

  // Check for same name but different PalCode - potential error (memoized)
  const palCodeConflicts = useMemo(() => {
    const conflicts = [];

    mergeOperations.forEach((op, opIndex) => {
      if (op.baseCard && op.cardsToMerge.length > 0) {
        const allCards = [op.baseCard, ...op.cardsToMerge];

        // Group by name
        const nameGroups = {};
        allCards.forEach(card => {
          const normalizedName = card.name.toLowerCase().trim();
          if (!nameGroups[normalizedName]) {
            nameGroups[normalizedName] = [];
          }
          nameGroups[normalizedName].push(card);
        });

        // Check for different PalCodes in same name group
        Object.entries(nameGroups).forEach(([name, cards]) => {
          if (cards.length > 1) {
            const palCodes = [...new Set(cards.map(c => c.palCode).filter(Boolean))];
            if (palCodes.length > 1) {
              conflicts.push({
                operationIndex: opIndex + 1,
                name: cards[0].name,
                cards: cards,
                palCodes: palCodes
              });
            }
          }
        });
      }
    });

    return conflicts;
  }, [mergeOperations]);

  const hasConflicts = useMemo(() => palCodeConflicts.length > 0, [palCodeConflicts]);

  // Filtered suggestions: same business ID only when accountCaseId not empty/0 (support both camelCase and PascalCase from API)
  const filteredSameCaseGroups = useMemo(() => {
    if (!mergeSuggestions?.sameAccountCaseIdGroups?.length) return [];
    return mergeSuggestions.sameAccountCaseIdGroups.filter((grp) => {
      const id = grp.accountCaseId ?? grp.AccountCaseId;
      if (!isMeaningfulAccountCaseId(id)) return false;
      return !onlyWithBusinessId || isMeaningfulAccountCaseId(id);
    });
  }, [mergeSuggestions?.sameAccountCaseIdGroups, onlyWithBusinessId]);

  const filteredSimilarNameGroups = useMemo(() => {
    if (!mergeSuggestions?.similarNameGroups?.length) return [];
    const threshold = nameAccuracyMin / 100;
    return mergeSuggestions.similarNameGroups
      .filter((grp) => (grp.score ?? 0) >= threshold)
      .slice(0, similarNameShowLimit);
  }, [mergeSuggestions?.similarNameGroups, nameAccuracyMin, similarNameShowLimit]);

  const activeFiltersCount = useMemo(() => {
    let n = 0;
    if (nameAccuracyMin !== nameAccuracyMinProp) n++;
    if (!onlyWithBusinessId) n++;
    return n;
  }, [nameAccuracyMin, nameAccuracyMinProp, onlyWithBusinessId]);

  const totalFilteredSimilarCount = useMemo(() => {
    if (!mergeSuggestions?.similarNameGroups?.length) return 0;
    const threshold = nameAccuracyMin / 100;
    return mergeSuggestions.similarNameGroups.filter((grp) => (grp.score ?? 0) >= threshold).length;
  }, [mergeSuggestions?.similarNameGroups, nameAccuracyMin]);

  const suggestionSearchLower = (suggestionsSearch || '').trim().toLowerCase();
  const searchFilteredSameCaseGroups = useMemo(() => {
    if (!suggestionSearchLower) return filteredSameCaseGroups;
    return filteredSameCaseGroups.filter((grp) => {
      const id = (grp.accountCaseId ?? grp.AccountCaseId ?? '').toLowerCase();
      return (grp.cards || []).some(
        (c) =>
          String(c.name || '').toLowerCase().includes(suggestionSearchLower) ||
          String(c.cardId || '').toLowerCase().includes(suggestionSearchLower) ||
          id.includes(suggestionSearchLower)
      );
    });
  }, [filteredSameCaseGroups, suggestionSearchLower]);

  const searchFilteredSimilarNameGroups = useMemo(() => {
    if (!suggestionSearchLower) return filteredSimilarNameGroups;
    return filteredSimilarNameGroups.filter((grp) =>
      (grp.cards || []).some(
        (c) =>
          String(c.name || '').toLowerCase().includes(suggestionSearchLower) ||
          String(c.cardId || '').toLowerCase().includes(suggestionSearchLower)
      )
    );
  }, [filteredSimilarNameGroups, suggestionSearchLower]);

  // Filtered operations for table (search by base card name/id or cards to merge)
  const filteredOperations = useMemo(() => {
    const q = (operationsSearch || '').trim().toLowerCase();
    if (!q) return mergeOperations;
    return mergeOperations.filter((op) => {
      const base = op.baseCard;
      const baseMatch = base && (String(base.name || '').toLowerCase().includes(q) || String(base.cardId || '').toLowerCase().includes(q));
      const mergeMatch = (op.cardsToMerge || []).some(
        (c) => String(c.name || '').toLowerCase().includes(q) || String(c.cardId || '').toLowerCase().includes(q)
      );
      return baseMatch || mergeMatch;
    });
  }, [mergeOperations, operationsSearch]);

  const validateOperations = () => {
    const errors = [];

    if (mergeOperations.length === 0) {
      errors.push(t('NoMergeOperationsDefined'));
      return errors;
    }

    mergeOperations.forEach((op, index) => {
      if (!op.baseCard) {
        errors.push(`${t('Operation')} ${index + 1}: ${t('BaseCardNotSelected')}`);
      }
      if (op.cardsToMerge.length === 0) {
        errors.push(`${t('Operation')} ${index + 1}: ${t('NoCardsSelectedToMerge')}`);
      }
      // Non-editable cards are allowed: merge runs but they are not deleted
    });

    return errors;
  };

  const showMergeConfirmation = async () => {
    const errors = validateOperations();
    if (errors.length > 0) {
      MySwal.fire({
        title: t('ValidationErrors'),
        html: errors.map(err => `<div style="text-align: left; margin: 5px 0;">• ${err}</div>`).join(''),
        icon: 'error',
        confirmButtonText: t('Close'),
      });
      return false;
    }

    // Check for PalCode conflicts
    const conflicts = palCodeConflicts;
    let conflictWarning = '';

    if (conflicts.length > 0) {
      conflictWarning = `
        <div style="background-color: #fff3cd; padding: 8px; border-radius: 6px; margin: 8px 0; border: 2px solid #ffc107; direction: rtl; text-align: right;">
          <div style="display: flex; align-items: center; margin-bottom: 6px; flex-direction: row-reverse;">
            <span style="font-size: 20px; margin-left: 10px;">⚠️</span>
            <strong style="color: #856404; font-size: 13px;">${t('PotentialMergeError')}</strong>
          </div>
          <p style="text-align: right; margin: 6px 0; font-size: 12px;">${t('CardsWithSameNameDifferentPalCode')}</p>
          <div style="max-height: 150px; overflow-y: auto;">
            ${conflicts.map(conflict => `
              <div style="background: white; padding: 6px; margin: 6px 0; border-radius: 4px; text-align: right; direction: rtl;">
                <strong style="font-size: 12px;">${t('Operation')} ${conflict.operationIndex}: ${conflict.name}</strong><br/>
                ${conflict.cards.map(card => `
                  <span style="margin: 3px; display: block; font-size: 11px;">
                    • ${card.cardId} - ${t('PalCode')}: <strong style="color: #ff9800; font-weight: bold; background-color: #fff3cd; padding: 2px 4px; border-radius: 3px;">${card.palCode || 'N/A'}</strong>
                  </span>
                `).join('')}
              </div>
            `).join('')}
          </div>
          <p style="text-align: right; color: #856404; margin-top: 6px; font-size: 11px;">
            <strong>${t('ReviewBeforeProceedingWithMerge')}</strong>
          </p>
        </div>
      `;
    }

    const totalCards = getTotalCardsToMerge();
    const totalOps = mergeOperations.length;

    const result = await MySwal.fire({
      title: t('MergeConfirmation'),
      html: `
        <div style="direction: rtl; text-align: right; max-height: 70vh; overflow-y: auto;">
          <div style="margin-bottom: 10px; text-align: center;">
            <h3 style="margin: 5px 0; font-size: 16px;">${t('YouAreAboutToMerge')}</h3>
            <p style="font-size: 14px; color: #1976d2; margin: 5px 0;">
              <strong>${totalOps}</strong> ${t('BaseCardsAnd')} 
              <strong>${totalCards}</strong> ${t('cardsToMerge')}
            </p>
          </div>
          
          ${conflictWarning}
          
          <div style="background-color: #e3f2fd; padding: 8px; border-radius: 6px; margin: 8px 0; max-height: 50vh; overflow-y: auto;">
            ${mergeOperations.map((op, index) => `
              <div style="margin: 6px 0; padding: 8px; background: white; border-radius: 4px; text-align: right; direction: rtl;">
                <strong style="font-size: 14px;">${t('Operation')} ${index + 1}:</strong><br/>
                <div style="margin: 6px 0;">
                  <span style="color: green; font-weight: bold; font-size: 13px;">✓ ${t('BaseCard')}:</span><br/>
                  <span style="margin-right: 15px; font-size: 12px;">${op.baseCard.name} (${op.baseCard.cardId})</span><br/>
                  <span style="margin-right: 15px; font-size: 12px;">${t('PalCode')}: <strong style="color: #1976d2; font-weight: bold;">${op.baseCard.palCode || 'N/A'}</strong></span>
                </div>
                <div style="margin: 6px 0;">
                  <span style="color: orange; font-weight: bold; font-size: 13px;">← ${t('CardsToMerge')} (${op.cardsToMerge.length}):</span><br/>
                  ${op.cardsToMerge.map(card => `
                    <span style="margin-right: 15px; display: block; margin-top: 3px; font-size: 12px;">
                      • ${card.name} (${card.cardId}) - ${t('PalCode')}: <strong style="color: #1976d2; font-weight: bold;">${card.palCode || 'N/A'}</strong>
                    </span>
                  `).join('')}
                </div>
              </div>
            `).join('')}
          </div>
          
          <div style="margin-top: 10px; padding: 8px; background-color: #f8d7da; border-radius: 6px; text-align: right; direction: rtl;">
            <strong style="color: #721c24; font-size: 13px;">⚠️ ${t('ThisActionCannotBeUndone')}</strong>
            <p style="margin-top: 5px; font-size: 12px;">${t('AllDocumentsWillBeUpdated')}</p>
          </div>
        </div>
      `,
      icon: conflicts.length > 0 ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonText: t('ProceedWithMerge'),
      cancelButtonText: t('CancelMerge'),
      confirmButtonColor: conflicts.length > 0 ? '#ff9800' : '#d33',
      cancelButtonColor: '#3085d6',
      width: '90%',
      maxWidth: '700px',
      customClass: {
        popup: 'swal2-rtl',
        htmlContainer: 'swal2-rtl'
      }
    });

    return result.isConfirmed;
  };

  const showMergeResults = (result) => {
    const successCount = result.successCount || 0;
    const failureCount = result.failureCount || 0;
    const results = result.results || [];

    MySwal.fire({
      title: t('MergeResults'),
      html: `
        <div style="direction: rtl; text-align: right; max-height: 70vh; overflow-y: auto;">
          <div style="margin-bottom: 10px;">
            <div style="padding: 8px; background-color: #d4edda; border-radius: 6px; margin-bottom: 6px;">
              <strong style="color: #155724; font-size: 13px;">✓ ${t('SuccessfulOperations')}: ${successCount}</strong>
            </div>
            ${failureCount > 0 ? `
              <div style="padding: 8px; background-color: #f8d7da; border-radius: 6px;">
                <strong style="color: #721c24; font-size: 13px;">✗ ${t('FailedOperations')}: ${failureCount}</strong>
              </div>
            ` : ''}
          </div>
          
          <div style="max-height: 50vh; overflow-y: auto;">
            ${results.map((res, index) => `
              <div style="padding: 8px; margin: 6px 0; border: 1px solid #ddd; border-radius: 6px; background: ${res.success ? '#f8fff8' : '#fff8f8'}; text-align: right; direction: rtl;">
                <strong style="font-size: 12px;">${t('Operation')} ${index + 1}: ${res.baseCardName || res.baseCardId}</strong>
                ${res.mergedCards ? `
                  <ul style="margin: 5px 0; padding-right: 20px; padding-left: 0; font-size: 11px; list-style-position: inside;">
                    ${res.mergedCards.map(card => `
                      <li style="color: ${card.success ? 'green' : 'red'}; text-align: right;">
                        <strong>${card.cardName || card.cardId}</strong> (${card.cardId}): ${card.message === 'MergedSuccessfully' ? t('MergedSuccessfully') : card.message}
                      </li>
                    `).join('')}
                  </ul>
                ` : `<p style="color: red; font-size: 11px; text-align: right;">${res.message}</p>`}
              </div>
            `).join('')}
          </div>
        </div>
      `,
      icon: failureCount === 0 ? 'success' : 'warning',
      confirmButtonText: t('Close'),
      width: '90%',
      maxWidth: '600px',
      customClass: {
        popup: 'swal2-rtl',
        htmlContainer: 'swal2-rtl'
      }
    });
  };

  const handleConfirmMerge = async () => {
    const confirmed = await showMergeConfirmation();
    if (!confirmed) return;

    setIsProcessing(true);

    try {
      // Format data for API
      const batchMergeData = {
        mergeOperations: mergeOperations.map(op => ({
          baseCardId: op.baseCard.cardId,
          cardsToMerge: op.cardsToMerge.map(card => card.cardId),
        })),
        deleteOldCards: deleteOldCards,
      };

      await handleBatchMerge(batchMergeData);

      // Note: The parent component will handle showing results via the API response
      // Reset and close
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      MySwal.fire({
        title: t('Error'),
        text: t('FailedToBatchMerge'),
        icon: 'error',
        confirmButtonText: t('Close'),
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setMergeOperations([]);
    setShowMiniModal(false);
    setMergeSuggestions(null);
    setMiniModalData(null);
    onClose();
  };

  const loadSuggestions = useCallback(async () => {
    if (!caseId || !token) return;
    setLoadingSuggestions(true);
    setMergeSuggestions(null);
    try {
      const res = await EnumsService.GetMergeSuggestions(caseId, token, nameAccuracyMin);
      if (res.ok) {
        const data = await res.json();
        setMergeSuggestions(data);
        setDismissedSuggestionKeys(new Set());
        setSimilarNameShowLimit(10);
        toast.success(t('SuggestionsLoaded') || 'Suggestions loaded', { position: 'top-center', autoClose: 2000 });
      }
    } catch (e) {
      toast.error(t('FailedToLoadSuggestions') || 'Failed to load suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  }, [caseId, token, nameAccuracyMin, t]);

  const dismissSuggestion = useCallback((key) => {
    setDismissedSuggestionKeys(prev => new Set([...prev, key]));
  }, []);

  const dismissSuggestionItem = useCallback((groupKey, cardId) => {
    setDismissedSuggestionItems(prev => new Set([...prev, `${groupKey}-${cardId}`]));
  }, []);

  const addOperationFromSuggestion = useCallback((cards) => {
    if (!cards?.length || !processedCardsData.length) return;
    const cardIds = cards.map(c => c.cardId);
    const fullCards = cardIds.map(id => processedCardsData.find(p => String(p.cardId) === String(id))).filter(Boolean);
    if (fullCards.length < 2) {
      toast.info(t('NeedAtLeastTwoCards') || 'Need at least two cards to merge');
      return;
    }
    const [baseCard, ...toMerge] = fullCards;
    setMergeOperations(prev => {
      const emptyIdx = prev.findIndex(op => !op.baseCard && op.cardsToMerge.length === 0);
      if (emptyIdx !== -1) {
        const updated = [...prev];
        updated[emptyIdx] = { ...updated[emptyIdx], baseCard, cardsToMerge: toMerge };
        return updated;
      }
      return [...prev, { id: Date.now(), baseCard, cardsToMerge: toMerge }];
    });
    toast.success(t('OperationAdded') || 'Merge operation added', { position: 'top-center', autoClose: 2000 });
  }, [processedCardsData, t]);

  const fetchSimilarCards = useCallback(async (minSim) => {
    if (!caseId || !token) return;
    setMiniModalLoading(true);
    try {
      const res = await EnumsService.GetSimilarCards(caseId, {
        minSimilarity: (minSim ?? miniModalMinSimilarity) / 100,
        maxSimilarPerCard: 5,
        excludeCardIds: allSelectedIds,
      }, token);
      if (res.ok) {
        const data = await res.json();
        setMiniModalData(data);
      }
    } catch (e) {
      toast.error(t('Error') || 'Error loading similar cards');
    } finally {
      setMiniModalLoading(false);
    }
  }, [caseId, token, miniModalMinSimilarity, allSelectedIds, t]);

  const openMiniModal = () => {
    setShowMiniModal(true);
    setMiniModalSearch('');
    fetchSimilarCards();
  };

  const closeMiniModal = () => {
    setShowMiniModal(false);
    setMiniModalSortOrder('desc');
    setMiniModalSelectedCards([]);
    setMiniModalSearch('');
    setMiniModalData(null);
  };

  const toggleCardSelection = (cardId) => {
    setMiniModalSelectedCards(prev => {
      if (prev.includes(cardId)) {
        return prev.filter(id => id !== cardId);
      } else {
        return [...prev, cardId];
      }
    });
  };

  const handleAddSelectedCardsAsOperations = () => {
    if (miniModalSelectedCards.length === 0) {
      toast.info(t('NoCardsSelected') || 'No cards selected', { position: "top-center", autoClose: 2000 });
      return;
    }

    // Build operations from selected cards, including their similar cards as cardsToMerge
    const opsToAdd = miniModalSelectedCards.map(cardId => {
      const entry = miniModalCardsWithSimilar.find(e => e.card.cardId === cardId);
      const baseCardData = processedCardsData.find(c => c.cardId === cardId);
      if (!baseCardData) return null;

      const toMerge = (entry?.similarCards || [])
        .map(sc => processedCardsData.find(c => c.cardId === sc.cardId))
        .filter(Boolean);

      return {
        id: Date.now() + Math.random(),
        baseCard: baseCardData,
        cardsToMerge: toMerge,
      };
    }).filter(Boolean);

    const createdCount = opsToAdd.length;

    if (createdCount === 0) {
      toast.info(t('NoCardsSelected') || 'No valid cards found', { position: "top-center", autoClose: 2000 });
      return;
    }

    setMergeOperations(prev => [...prev, ...opsToAdd]);
    toast.success(`${createdCount} ${t('Operations')} ${t('Created') || 'created'}`, {
      position: "top-center",
      autoClose: 2000
    });
    
    closeMiniModal();
  };

  const selectedACardFromSimilarList = (cardId) => {
    closeMiniModal();
    // Find the card and add it to an empty operation or create a new one
    const card = processedCardsData.find(c => c.cardId === cardId);
    if (card) {
      // Check if there's an empty operation (one without a base card)
      const emptyOperation = mergeOperations.find(op => !op.baseCard);

      if (emptyOperation) {
        // Fill the empty operation
        updateOperation(emptyOperation.id, 'baseCard', card);
        const operationIndex = mergeOperations.findIndex(op => op.id === emptyOperation.id);
        toast.success(`${t('Operation')} ${operationIndex + 1}: ${card.name}`, { position: "top-center", autoClose: 2000 });
      } else {
        // Create a new operation if all existing operations have base cards
        const newOperation = {
          id: Date.now(),
          baseCard: card,
          cardsToMerge: [],
        };
        setMergeOperations([...mergeOperations, newOperation]);
        toast.success(`${t('Operation')} ${mergeOperations.length + 1}: ${card.name}`, { position: "top-center", autoClose: 2000 });
      }
    }
  };

  const getTotalCardsToMerge = () => {
    return mergeOperations.reduce((total, op) => total + op.cardsToMerge.length, 0);
  };

  return (
    <>
      <Dialog
        open={isVisible}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: '95vh',
            direction: 'ltr',
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '2px solid #1976d2', py: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" flexDirection="row-reverse">
              <MergeIcon sx={{ ml: 1, color: '#1976d2' }} />
              <Typography variant="h6" fontWeight="bold">
                {t('BatchMergeAccountCards')}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {hasConflicts && (
                <Chip
                  icon={<WarningIcon />}
                  label={t('SameNameDifferentPalCode')}
                  color="warning"
                  size="small"
                />
              )}
              <Chip
                label={`${mergeOperations.length} ${t('Operations')} | ${getTotalCardsToMerge()} ${t('Cards')}`}
                color="primary"
                variant="outlined"
              />
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 1.5, overflowY: 'auto', maxHeight: 'calc(95vh - 200px)' }} dir={direction}>
          {cardsDataLoading || (isVisible && accountCardsData?.length > 0 && processedCardsData.length === 0) ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, gap: 2 }}>
              <CircularProgress size={48} />
              <Typography variant="body2" color="textSecondary">{t('Loading')}...</Typography>
            </Box>
          ) : (
          <>
          {/* Instructions */}
          {/* <Alert severity="info" sx={{ mb: 1.5,  textAlign: 'right' }}>
            <AlertTitle sx={{  textAlign: 'right' }}>{t('HowToUse')}</AlertTitle>
            <Typography variant="body2" sx={{  textAlign: 'right' }}>
              1. {t('SelectBaseCard')}<br />
              2. {t('SelectCardsToMerge')}<br />
              3. {t('AddMoreOperations')}<br />
              4. {t('PreviewAndConfirm')}
            </Typography>
          </Alert> */}

          {/* Load merge suggestions from backend */}
          <Box sx={{ mb: 1.5, mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Button
              onClick={loadSuggestions}
              variant="outlined"
              disabled={!caseId || !token || isProcessing || loadingSuggestions}
              startIcon={loadingSuggestions ? <CircularProgress size={18} /> : <MergeIcon />}
              sx={{ textTransform: 'none' }}
            >
              {loadingSuggestions ? (t('Loading') || 'Loading...') : (t('LoadMergeSuggestions') || 'Load merge suggestions')}
            </Button>
            <Button
              onClick={openMiniModal}
              variant="outlined"
              endIcon={<ListIcon sx={{ ml: 1 }} />}
              disabled={isProcessing}
              sx={{ textTransform: 'none' }}
            >
              {t('ViewCardsWithSimilarCards')}
            </Button>
            {mergeSuggestions && (
              <Button
                onClick={() => setSuggestionsFiltersOpen((o) => !o)}
                variant="outlined"
                size="small"
                startIcon={<FilterListIcon />}
                endIcon={suggestionsFiltersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: 'none' }}
              >
                {t('Filters') || 'Filters'}
                {activeFiltersCount > 0 && (
                  <Chip label={activeFiltersCount} size="small" color="primary" sx={{ ml: 1, height: 20, fontSize: '0.75rem' }} />
                )}
              </Button>
            )}
          </Box>

          {/* Collapsible filters for suggestions */}
          {mergeSuggestions && (
            <Collapse in={suggestionsFiltersOpen}>
              <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} flexWrap="wrap" alignItems="flex-start">
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                      {t('NameAccuracyMin') || 'Name accuracy (min %)'}: {nameAccuracyMin}%
                    </Typography>
                    <Slider
                      value={nameAccuracyMin}
                      onChange={(_, v) => setNameAccuracyMin(v)}
                      min={0}
                      max={100}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => `${v}%`}
                      size="small"
                      sx={{ maxWidth: 220 }}
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={onlyWithBusinessId}
                        onChange={(e) => setOnlyWithBusinessId(e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">{t('OnlyWithBusinessId') || 'Same business ID only (when not empty)'}</Typography>}
                  />
                </Stack>
              </Paper>
            </Collapse>
          )}

          {/* Search within suggestions */}
          {mergeSuggestions && (filteredSameCaseGroups.length > 0 || (mergeSuggestions.similarNameGroups?.length > 0)) && (
            <TextField
              size="small"
              fullWidth
              placeholder={t('SearchSuggestions') || 'Search suggestions (name, card ID, business ID)'}
              value={suggestionsSearch}
              onChange={(e) => setSuggestionsSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.5, maxWidth: 400 }}
            />
          )}

          {/* Merge suggestions: Same AccountCaseId and Similar names (filtered) */}
          {mergeSuggestions && (
            <Box sx={{ mb: 2 }}>
              {searchFilteredSameCaseGroups.length > 0 && (
                <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                  <Box
                    onClick={() => setSameCaseSectionOpen((o) => !o)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': { bgcolor: 'primary.dark' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {t('SameAccountCaseId') || 'Same business ID'}
                      </Typography>
                      <Chip
                        label={searchFilteredSameCaseGroups.length}
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'inherit', fontWeight: 700, height: 22 }}
                      />
                    </Box>
                    <IconButton size="small" sx={{ color: 'inherit' }} aria-label={sameCaseSectionOpen ? 'Collapse' : 'Expand'}>
                      {sameCaseSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  <Collapse in={sameCaseSectionOpen}>
                    <Box sx={{ p: 1.5, pt: 1 }}>
                      <Stack spacing={1.5}>
                        {searchFilteredSameCaseGroups
                          .map((grp, idx) => {
                            const originalIdx = mergeSuggestions.sameAccountCaseIdGroups.indexOf(grp);
                            return { grp, idx: originalIdx >= 0 ? originalIdx : idx, groupKey: `same-${originalIdx >= 0 ? originalIdx : idx}` };
                          })
                          .filter(({ groupKey }) => !dismissedSuggestionKeys.has(groupKey))
                          .map(({ grp, groupKey }) => {
                            const remainingCards = (grp.cards || []).filter((c) => !dismissedSuggestionItems.has(`${groupKey}-${c.cardId}`));
                            if (remainingCards.length === 0) return null;
                            const accountCaseId = grp.accountCaseId ?? grp.AccountCaseId ?? '';
                            const baseIdx = selectedBaseIndexByKey[groupKey] ?? 0;
                            return (
                              <SuggestionGroupPicker
                                key={groupKey}
                                cards={remainingCards.map(c => ({ ...c, accountCaseId: c.accountCaseId ?? accountCaseId }))}
                                baseIndex={baseIdx}
                                onBaseChange={(idx) => setSelectedBaseIndexByKey((prev) => ({ ...prev, [groupKey]: idx }))}
                                onRemoveCard={(cardId) => dismissSuggestionItem(groupKey, cardId)}
                                onUseMerge={(ordered) => { addOperationFromSuggestion(ordered); dismissSuggestion(groupKey); }}
                                onDismissGroup={() => dismissSuggestion(groupKey)}
                                groupBadge={`${t('AccountCaseId') || 'Business ID'}: ${accountCaseId}`}
                                groupBadgeColor="primary"
                                isProcessing={isProcessing}
                                t={t}
                              />
                            );
                          })}
                      </Stack>
                    </Box>
                  </Collapse>
                </Paper>
              )}
              {(mergeSuggestions.similarNameGroups?.length > 0) && (
                <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                  <Box
                    onClick={() => setSimilarNamesSectionOpen((o) => !o)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      bgcolor: 'secondary.main',
                      color: 'secondary.contrastText',
                      '&:hover': { bgcolor: 'secondary.dark' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {t('SimilarNames') || 'Similar names'}
                      </Typography>
                      <Chip
                        label={searchFilteredSimilarNameGroups.length}
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'inherit', fontWeight: 700, height: 22 }}
                      />
                    </Box>
                    <IconButton size="small" sx={{ color: 'inherit' }} aria-label={similarNamesSectionOpen ? 'Collapse' : 'Expand'}>
                      {similarNamesSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  <Collapse in={similarNamesSectionOpen}>
                    <Box sx={{ p: 1.5, pt: 1 }}>
                      <Stack spacing={1.5}>
                        {searchFilteredSimilarNameGroups
                          .map((grp, idx) => {
                            const originalIdx = mergeSuggestions.similarNameGroups.indexOf(grp);
                            return { grp, idx: originalIdx >= 0 ? originalIdx : idx, groupKey: `similar-${originalIdx >= 0 ? originalIdx : idx}` };
                          })
                          .filter(({ groupKey }) => !dismissedSuggestionKeys.has(groupKey))
                          .map(({ grp, groupKey }) => {
                            const remainingCards = (grp.cards || []).filter((c) => !dismissedSuggestionItems.has(`${groupKey}-${c.cardId}`));
                            if (remainingCards.length === 0) return null;
                            const baseIdx = selectedBaseIndexByKey[groupKey] ?? 0;
                            const maxScore = grp.score != null ? `${(grp.score * 100).toFixed(0)}%` : null;
                            return (
                              <SuggestionGroupPicker
                                key={groupKey}
                                cards={remainingCards}
                                baseIndex={baseIdx}
                                onBaseChange={(idx) => setSelectedBaseIndexByKey((prev) => ({ ...prev, [groupKey]: idx }))}
                                onRemoveCard={(cardId) => dismissSuggestionItem(groupKey, cardId)}
                                onUseMerge={(ordered) => { addOperationFromSuggestion(ordered); dismissSuggestion(groupKey); }}
                                onDismissGroup={() => dismissSuggestion(groupKey)}
                                maxGroupScore={maxScore}
                                groupBadgeColor="secondary"
                                isProcessing={isProcessing}
                                t={t}
                              />
                            );
                          })}
                      </Stack>
                      {totalFilteredSimilarCount > similarNameShowLimit && (
                        <Button
                          size="small"
                          onClick={() => setSimilarNameShowLimit((prev) => Math.min(totalFilteredSimilarCount, prev + 20))}
                          sx={{ mt: 1, textTransform: 'none' }}
                        >
                          {t('ShowMore') || 'Show more'} (+{Math.min(20, totalFilteredSimilarCount - similarNameShowLimit)})
                        </Button>
                      )}
                    </Box>
                  </Collapse>
                </Paper>
              )}
              {mergeSuggestions && (!filteredSameCaseGroups.length && !filteredSimilarNameGroups.length) && (
                <Typography variant="body2" color="textSecondary">{t('NoMergeSuggestions') || 'No merge suggestions found.'}</Typography>
              )}
            </Box>
          )}

          {/* PalCode Conflict Warning */}
          {hasConflicts && (
            <Alert severity="warning" sx={{ mb: 1.5, textAlign: 'right' }}>
              <AlertTitle sx={{ textAlign: 'right' }}>{t('PotentialMergeError')}</AlertTitle>
              <Typography variant="body2" sx={{ textAlign: 'right' }}>
                {t('CardsWithSameNameDifferentPalCode')}
              </Typography>
            </Alert>
          )}

          {/* Delete Option */}
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={deleteOldCards}
                  onChange={(e) => setDeleteOldCards(e.target.checked)}
                  color="primary"
                  disabled={isProcessing}
                />
              }
              disabled={isProcessing}
              label={
                <Box display="flex" alignItems="center">
                  <Typography>{t('DeleteMergedCards')}</Typography>
                  <Tooltip title={t('DeleteMergedCardsTooltip')}>
                    <InfoIcon sx={{ ml: 1, fontSize: 18, color: 'gray' }} />
                  </Tooltip>
                </Box>
              }
            />
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          {/* Search operations (big list: find by base card or card to merge) */}
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              placeholder={t('SearchOperations') || 'Search operations (base card or card to merge)'}
              value={operationsSearch}
              onChange={(e) => setOperationsSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 360 }}
            />
            {operationsSearch.trim() && (
              <Typography variant="caption" color="textSecondary">
                {filteredOperations.length} / {mergeOperations.length} {t('Operations')}
              </Typography>
            )}
          </Box>

          {/* Merge Operations */}
          {mergeOperations.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', bgcolor: '#fafafa', borderStyle: 'dashed' }}>
              <MergeIcon
                sx={{
                  fontSize: 48,
                  color: 'primary.main',
                  mb: 1,
                  animation: `${mergeArrowFloat} 1.6s ease-in-out infinite`,
                }}
              />
              <Typography variant="body1" color="textSecondary" gutterBottom>
                {t('NoOperationsYet') || 'No merge operations yet'}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                {t('AddOperationsHint') || 'Use suggestions above, the similar cards modal, or add a merge'}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={addNewOperation}
                disabled={isProcessing}
                sx={{ textTransform: 'none' }}
              >
                {t('AddMerge') || 'Add Merge'}
              </Button>
            </Paper>
          ) : (
            <Stack spacing={1.5}>
              {filteredOperations.map((operation) => {
                const realOpIndex = mergeOperations.findIndex((op) => op.id === operation.id) + 1;
                const operationConflicts = palCodeConflicts.filter(c => c.operationIndex === realOpIndex);
                const hasOperationConflict = operationConflicts.length > 0;
                const hasSimilar = similarCardsMap[operation.id]?.length > 0;

                return (
                  <Paper
                    key={operation.id}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderColor: hasOperationConflict ? '#ff9800' : operation.baseCard ? '#1976d2' : '#e0e0e0',
                      borderWidth: operation.baseCard ? 1.5 : 1,
                      bgcolor: hasOperationConflict ? '#fff8e1' : 'white',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip label={`#${realOpIndex}`} size="small" color="primary" sx={{ fontWeight: 700, fontSize: 12, height: 24 }} />
                        {operation.baseCard && (
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            {operation.baseCard.name}
                            {operation.cardsToMerge.length > 0 && ` → ${operation.cardsToMerge.length} ${t('Cards')}`}
                          </Typography>
                        )}
                        {hasOperationConflict && (
                          <Chip icon={<WarningIcon sx={{ fontSize: 14 }} />} label={t('PalCodeConflict') || 'PalCode conflict'} size="small" color="warning" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                        )}
                      </Box>
                      <Tooltip title={t('RemoveOperation')}>
                        <IconButton onClick={() => removeOperation(operation.id)} color="error" size="small" disabled={isProcessing}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1976d2', fontSize: 11, mb: 0.5, display: 'block' }}>
                        {t('BaseCard')}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <AutocompleteSelect
                            options={availableBaseCardsMap[operation.id] ?? []}
                            selectedValues={operation.baseCard}
                            onChange={(event, newValue) => updateOperation(operation.id, 'baseCard', newValue)}
                            placeholder={t('SelectBaseCard')}
                            isMultiple={false}
                            width="100%"
                            disabled={isProcessing}
                          />
                        </Box>
                        {operation.baseCard?.palCode && (
                          <Chip size="small" label={`${t('PalCode')}: ${operation.baseCard.palCode}`} sx={{ fontSize: 10, height: 22, bgcolor: '#e3f2fd', color: '#1976d2', flexShrink: 0 }} />
                        )}
                        {operation.baseCard && isMeaningfulAccountCaseId(operation.baseCard.accountCaseId) && (
                          <Chip size="small" label={`${t('AccountCaseId')}: ${operation.baseCard.accountCaseId}`} sx={{ fontSize: 10, height: 22 }} variant="outlined" />
                        )}
                      </Box>
                    </Box>

                    {operation.cardsToMerge.length > 0 && (
                      <Box sx={{ mb: 1, p: 1, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffe0b2' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#e65100', fontSize: 11 }}>
                            {t('CardsToMerge')} ({operation.cardsToMerge.length})
                          </Typography>
                          {operation.cardsToMerge.length > 1 && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => updateOperation(operation.id, 'cardsToMerge', [])}
                              disabled={isProcessing}
                              sx={{ textTransform: 'none', fontSize: 10, py: 0, minHeight: 0, lineHeight: 1.5 }}
                            >
                              {t('RemoveAll') || 'Remove all'}
                            </Button>
                          )}
                        </Box>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {operation.cardsToMerge.map(card => {
                            const score = card.similarityScore != null
                              ? card.similarityScore
                              : (operation.baseCard ? getCompositeSimilarityScore(operation.baseCard, card) : null);
                            const scoreLabel = score != null && score > 0 ? ` • ${(score * 100).toFixed(0)}%` : '';
                            return (
                              <Tooltip
                                key={card.cardId}
                                arrow
                                title={
                                  <Box sx={{ fontSize: 12 }}>
                                    <strong>{card.name}</strong><br />
                                    {t('Card ID')}: {card.cardId}
                                    {score != null && score > 0 ? <><br />{t('similar')}: {(score * 100).toFixed(0)}%</> : null}
                                    {card.palCode ? <><br />{t('PalCode')}: {card.palCode}</> : null}
                                    {isMeaningfulAccountCaseId(card.accountCaseId) ? <><br />{t('AccountCaseId')}: {card.accountCaseId}</> : null}
                                    <br />{t('ClickToSetAsBase')}
                                  </Box>
                                }
                              >
                                <Chip
                                  size="small"
                                  label={formatSuggestionCardLabel(card, t) + scoreLabel}
                                  color={getMatchScoreColor(score)}
                                  variant="filled"
                                  icon={<StarIcon sx={{ fontSize: '14px !important' }} />}
                                  onClick={() => !isProcessing && promoteToBase(operation.id, card)}
                                  onDelete={isProcessing ? undefined : () => {
                                    updateOperation(operation.id, 'cardsToMerge', operation.cardsToMerge.filter(c => c.cardId !== card.cardId));
                                  }}
                                  deleteIcon={<ClearIcon sx={{ fontSize: 14 }} />}
                                  sx={{ fontSize: 11, height: 26, cursor: 'pointer', maxWidth: 320, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                                />
                              </Tooltip>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}

                    <Box sx={{ mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', fontSize: 11, mb: 0.5, display: 'block' }}>
                        {t('AddCardsToMerge') || 'Add cards to merge'}
                      </Typography>
                      <AutocompleteSelect
                        options={availableCardsForMergeMap[operation.id] ?? []}
                        selectedValues={null}
                        onChange={(event, newValue) => {
                          if (newValue && !operation.cardsToMerge.find(c => c.cardId === newValue.cardId)) {
                            updateOperation(operation.id, 'cardsToMerge', [...operation.cardsToMerge, newValue]);
                          }
                        }}
                        placeholder={t('SearchAndAddCard') || 'Search card to add...'}
                        isMultiple={false}
                        width="100%"
                        disabled={isProcessing}
                      />
                    </Box>

                    {operation.baseCard && hasSimilar && (
                      <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed #e0e0e0' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11, color: '#888', mb: 0.5, display: 'block' }}>
                          {t('SuggestedSimilarCards')}:
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {similarCardsMap[operation.id].slice(0, 5).map(card => {
                            const alreadyAdded = operation.cardsToMerge.some(c => c.cardId === card.cardId);
                            return (
                              <Tooltip
                                key={card.cardId}
                                arrow
                                title={
                                  <Box sx={{ fontSize: 12 }}>
                                    <strong>{card.name}</strong><br />
                                    {t('Card ID')}: {card.cardId}<br />
                                    {t('similar')}: {(card.similarityScore * 100).toFixed(0)}%
                                    {card.palCode ? <><br />{t('PalCode')}: {card.palCode}</> : null}
                                    {isMeaningfulAccountCaseId(card.accountCaseId) ? <><br />{t('AccountCaseId')}: {card.accountCaseId}</> : null}
                                    {alreadyAdded ? <><br /><em>{t('AlreadyAdded') || 'Already added'}</em></> : null}
                                  </Box>
                                }
                              >
                                <Chip
                                  size="small"
                                  icon={alreadyAdded ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : undefined}
                                  label={formatSuggestionCardLabel(card, t, { includeScore: true })}
                                  variant={alreadyAdded ? 'filled' : 'outlined'}
                                  color={alreadyAdded ? 'success' : getMatchScoreColor(card.similarityScore)}
                                  onClick={() => !isProcessing && !alreadyAdded && addSimilarCardToMerge(operation.id, card)}
                                  sx={{
                                    cursor: isProcessing || alreadyAdded ? 'default' : 'pointer',
                                    fontSize: 11, height: 24,
                                    opacity: alreadyAdded ? 0.7 : 1,
                                    '&:hover': { bgcolor: alreadyAdded ? undefined : '#e3f2fd' },
                                  }}
                                />
                              </Tooltip>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          )}

          {/* Add Operation Button */}
          {mergeOperations.length > 0 && (
            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addNewOperation}
                disabled={isProcessing}
                sx={{ textTransform: 'none' }}
              >
                {t('AddMerge') || 'Add Merge'}
              </Button>
            </Box>
          )}
          </>
          )}
        </DialogContent>

        <DialogActions sx={{ direction: 'ltr', justifyContent: 'space-between', px: 3, py: 2, bgcolor: '#f5f5f5' }}>
          <Box>
            <Typography variant="caption" color="textSecondary">
              {t('TotalOperations')}: {mergeOperations.length} | {' '}
              {t('TotalCards')}: {getTotalCardsToMerge()}
              {hasConflicts && (
                <span style={{ color: '#ff9800', marginLeft: '10px' }}>
                  ⚠️ {t('PotentialMergeError')}
                </span>
              )}
            </Typography>
          </Box>
          <Box>
            <Button
              onClick={handleConfirmMerge}
              variant="contained"
              color={hasConflicts ? 'warning' : 'primary'}
              startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <MergeIcon />}
              disabled={isProcessing || mergeOperations.length === 0}
              sx={{ mr: 1 }}
            >
              {isProcessing ? (t('Merging')) : (t('ConfirmMerge'))}
            </Button>
            <Button
              onClick={handleClose}
              variant="outlined"
              startIcon={<ClearIcon />}
              disabled={isProcessing}
            >
              {t('cancelButton')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Mini Modal – Similar Cards (backend-powered) */}
      {showMiniModal && (
        <Dialog
          open={showMiniModal}
          onClose={closeMiniModal}
          maxWidth="md"
          fullWidth
          sx={{ '& .MuiDialog-paper': { maxHeight: '90vh', display: 'flex', flexDirection: 'column' } }}
          dir={direction}
        >
          <DialogTitle
            sx={{
              bgcolor: '#1976d2', color: 'white', py: 1.5,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <ListIcon />
              <Typography variant="h6" fontWeight="bold">
                {t('ViewCardsWithSimilarCards')}
              </Typography>
              {miniModalData && (
                <Chip
                  label={`${miniModalCardsWithSimilar.length} / ${miniModalData.totalCards}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600 }}
                />
              )}
            </Box>
            <IconButton onClick={closeMiniModal} sx={{ color: 'white' }} size="small">
              <ClearIcon />
            </IconButton>
          </DialogTitle>

          {/* Toolbar: search, similarity slider, sort, select all */}
          <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', flexShrink: 0, bgcolor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
            <TextField
              size="small"
              placeholder={t('Search') || 'Search...'}
              value={miniModalSearch}
              onChange={(e) => setMiniModalSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              }}
              sx={{ minWidth: 180, flex: '1 1 180px', maxWidth: 280 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200, flex: '1 1 200px', maxWidth: 300 }}>
              <Typography variant="caption" color="textSecondary" noWrap sx={{ minWidth: 'fit-content' }}>
                {t('MinimumSimilarCards') || 'Min similarity'}: {miniModalMinSimilarity}%
              </Typography>
              <Slider
                size="small"
                value={miniModalMinSimilarity}
                onChange={(_, v) => setMiniModalMinSimilarity(v)}
                onChangeCommitted={(_, v) => fetchSimilarCards(v)}
                min={20}
                max={95}
                step={5}
                sx={{ flex: 1 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                size="small"
                variant={miniModalSortOrder === 'desc' ? 'contained' : 'outlined'}
                onClick={() => setMiniModalSortOrder('desc')}
                sx={{ minWidth: 40, px: 1, fontSize: 11 }}
              >
                {t('Descending')}
              </Button>
              <Button
                size="small"
                variant={miniModalSortOrder === 'asc' ? 'contained' : 'outlined'}
                onClick={() => setMiniModalSortOrder('asc')}
                sx={{ minWidth: 40, px: 1, fontSize: 11 }}
              >
                {t('Ascending')}
              </Button>
            </Box>
            {miniModalCardsWithSimilar.length > 0 && (
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  const allIds = miniModalCardsWithSimilar.map(e => e.card.cardId);
                  const allSelected = allIds.every(id => miniModalSelectedCards.includes(id));
                  setMiniModalSelectedCards(allSelected ? [] : allIds);
                }}
                sx={{ textTransform: 'none', fontSize: 12, whiteSpace: 'nowrap' }}
              >
                {miniModalCardsWithSimilar.every(e => miniModalSelectedCards.includes(e.card.cardId))
                  ? (t('DeselectAll') || 'Deselect all')
                  : (t('SelectAll') || 'Select all')}
              </Button>
            )}
          </Box>

          <DialogContent sx={{ p: 0, flex: 1, overflow: 'auto' }}>
            {miniModalLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                <CircularProgress />
                <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                  {t('Loading') || 'Loading...'}
                </Typography>
              </Box>
            ) : miniModalCardsWithSimilar.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body1" color="textSecondary">
                  {t('NoSimilarCardsFound')}
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ fontWeight: 700, fontSize: 12, bgcolor: '#f5f5f5' }} />
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, bgcolor: '#f5f5f5', whiteSpace: 'nowrap' }}>{t('Name')}</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, bgcolor: '#f5f5f5', whiteSpace: 'nowrap' }}>{t('Card ID')}</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, bgcolor: '#f5f5f5', whiteSpace: 'nowrap' }}>{t('PalCode')}</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, bgcolor: '#f5f5f5', whiteSpace: 'nowrap' }}>{t('AccountCaseId')}</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, bgcolor: '#f5f5f5', whiteSpace: 'nowrap' }}>{t('similar')}</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: 12, bgcolor: '#f5f5f5', minWidth: 200 }}>{t('SimilarCards')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {miniModalCardsWithSimilar.map(({ card, similarCards, highestSimilarityScore }) => {
                      const isSelected = miniModalSelectedCards.includes(card.cardId);
                      return (
                        <TableRow
                          key={card.cardId}
                          hover
                          selected={isSelected}
                          onClick={() => toggleCardSelection(card.cardId)}
                          sx={{ cursor: 'pointer', '&.Mui-selected': { bgcolor: '#e3f2fd' } }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onChange={() => toggleCardSelection(card.cardId)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, fontWeight: 600 }}>
                            {card.name}
                          </TableCell>
                          <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, whiteSpace: 'nowrap' }}>
                            {card.cardId}
                          </TableCell>
                          <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, whiteSpace: 'nowrap' }}>
                            {card.palCode || '-'}
                          </TableCell>
                          <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, whiteSpace: 'nowrap' }}>
                            {isMeaningfulAccountCaseId(card.accountCaseId) ? card.accountCaseId : '-'}
                          </TableCell>
                          <TableCell sx={{ py: 0.5, px: 1, fontSize: 12, whiteSpace: 'nowrap' }}>
                            <Chip
                              label={`${(highestSimilarityScore * 100).toFixed(0)}%`}
                              size="small"
                              color={highestSimilarityScore >= 0.8 ? 'error' : highestSimilarityScore >= 0.6 ? 'warning' : 'default'}
                              sx={{ fontWeight: 600, fontSize: 11, height: 22 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.5, px: 1, fontSize: 12 }}>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {similarCards.map((sc) => (
                                <Tooltip
                                  key={sc.cardId}
                                  arrow
                                  title={
                                    <Box sx={{ fontSize: 12 }}>
                                      <strong>{sc.name}</strong><br />
                                      {t('Card ID')}: {sc.cardId}<br />
                                      {t('similar')}: {(sc.similarityScore * 100).toFixed(0)}%<br />
                                      {t('PalCode')}: {sc.palCode || 'N/A'}
                                      {isMeaningfulAccountCaseId(sc.accountCaseId) ? <><br />{t('AccountCaseId')}: {sc.accountCaseId}</> : null}
                                    </Box>
                                  }
                                >
                                  <Chip
                                    label={formatSuggestionCardLabel(sc, t, { includeScore: true })}
                                    size="small"
                                    variant="outlined"
                                    color={getMatchScoreColor(sc.similarityScore)}
                                    sx={{ fontSize: 10, height: 20, cursor: 'default', '&:hover': { bgcolor: '#e3f2fd' } }}
                                  />
                                </Tooltip>
                              ))}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 2, py: 1.5, bgcolor: '#f5f5f5', justifyContent: 'space-between', flexShrink: 0, borderTop: '1px solid #e0e0e0' }}>
            <Box>
              {miniModalSelectedCards.length > 0 && (
                <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                  {miniModalSelectedCards.length} {t('Cards')} {t('Selected') || 'selected'}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={handleAddSelectedCardsAsOperations}
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                disabled={miniModalSelectedCards.length === 0}
              >
                {t('AddAsOperations') || t('Add')} ({miniModalSelectedCards.length})
              </Button>
              <Button
                onClick={closeMiniModal}
                variant="outlined"
                startIcon={<ClearIcon />}
              >
                {t('Close')}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default BatchMergeAccountCards;
